import { ParchmentCreatePrescriptionButtonComponent } from '@principle-theorem/ng-principle-shared';
import { IDynamicComponentRenderer } from '@principle-theorem/ng-shared';
import {
  CLINIVIEW_INTEGRATION,
  DENTALEYE_INTEGRATION,
  DTX_STUDIO_DRIVER_DIRECT_INTEGRATION,
  DTX_STUDIO_INTEGRATION,
  EZDENT_INTEGRATION,
  IDIXEL_INTEGRATION,
  ROMEXIS_INTEGRATION,
  ROMEXIS_V2_INTEGRATION,
  SIDEXIS_INTEGRATION,
  TWAIN_INTEGRATION,
  VISTASOFT_INTEGRATION,
  VIXWIN_INTEGRATION,
} from '@principle-theorem/principle-bridge-core';
import { BridgeDeviceFeature } from '@principle-theorem/principle-core';
import { PARCHMENT_FEATURE } from '@principle-theorem/principle-core/features';
import { AppIntegrationFeature } from '@principle-theorem/principle-core/interfaces';
import { CliniviewOpenPatientComponent } from './components/integrations/cliniview/cliniview-open-patient/cliniview-open-patient.component';
import { DentalEyeOpenPatientComponent } from './components/integrations/dentaleye/dentaleye-open-patient/dentaleye-open-patient.component';
import { DtxStudioDriverDirectTriggerComponent } from './components/integrations/dtx-studio-driver-direct/dtx-studio-driver-direct-trigger.component';
import { DtxStudioIntegrationComponent } from './components/integrations/dtx-studio/dtx-studio-integration/dtx-studio-integration.component';
import { EzdentTriggerXRayComponent } from './components/integrations/ezdent/ezdent-trigger-x-ray/ezdent-trigger-x-ray.component';
import { IDixelOpenPatientComponent } from './components/integrations/idixel/idixel-open-patient/idixel-open-patient.component';
import { RomexisOpenPatientComponent } from './components/integrations/romexis/romexis-open-patient/romexis-open-patient.component';
import { RomexisV2OpenPatientComponent } from './components/integrations/romexis-v2/romexis-v2-open-patient/romexis-v2-open-patient.component';
import { SidexisTriggerXRayComponent } from './components/integrations/sidexis/sidexis-trigger-x-ray/sidexis-trigger-x-ray.component';
import { TwainTriggerScanComponent } from './components/integrations/twain/twain-trigger-scan/twain-trigger-scan.component';
import { VistaSoftOpenPatientComponent } from './components/integrations/vistasoft/vistasoft-open-patient/vistasoft-open-patient.component';
import { VixWinOpenPatientComponent } from './components/integrations/vixwin/vixwin-open-patient/vixwin-open-patient.component';
import { IIntegrationActionData } from './integration-action-data';

export const INTEGRATION_ACTION_RENDERERS: IDynamicComponentRenderer<
  unknown,
  IIntegrationActionData
>[] = [
  {
    component: SidexisTriggerXRayComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, SIDEXIS_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: EzdentTriggerXRayComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, EZDENT_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: TwainTriggerScanComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, TWAIN_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: RomexisOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, ROMEXIS_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: RomexisV2OpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, ROMEXIS_V2_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: VixWinOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, VIXWIN_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: CliniviewOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, CLINIVIEW_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: DentalEyeOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, DENTALEYE_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: IDixelOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, IDIXEL_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: VistaSoftOpenPatientComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, VISTASOFT_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: DtxStudioIntegrationComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(feature, DTX_STUDIO_INTEGRATION) &&
          BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: DtxStudioDriverDirectTriggerComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.MediaCapture &&
      data.availableFeatures.some(
        (feature) =>
          BridgeDeviceFeature.isIntegration(
            feature,
            DTX_STUDIO_DRIVER_DIRECT_INTEGRATION
          ) && BridgeDeviceFeature.isEnabled(feature)
      ),
  },
  {
    component: ParchmentCreatePrescriptionButtonComponent,
    isFor: (data: IIntegrationActionData) =>
      data.appIntegrationFeature === AppIntegrationFeature.CreatePrescription,
    feature: PARCHMENT_FEATURE,
  },
];
